<template>
  <div v-if="isAuthenticated">
    <router-view />
  </div>
  <v-container v-else-if="handoffMessage">
    <v-alert color="semantic-warning" variant="tonal" icon="mdi-alert-outline">
      {{ handoffMessage }}
    </v-alert>
  </v-container>
</template>

<script type="ts">
import AuthService from "@/api/AuthService"
import { computed, onMounted, ref } from "vue"
import { useRoute } from "vue-router"

/**
 * Shown instead of bouncing the user, per failure reason of the exchange.
 *
 * Deliberately without a number of seconds: the reset header would have to be
 * exposed through CORS first, and at 120 requests per minute the case should
 * not come up any more. Deliberately without an automatic second attempt too -
 * that is what hid the problem in the first place.
 */
const HANDOFF_MESSAGES = {
  "rate-limited":
    "Es wurden zu viele Modulaufrufe in kurzer Zeit gemacht. Bitte den Menüpunkt in einem Moment noch einmal anklicken.",
  unavailable:
    "Der Einstieg in das Modul hat nicht geklappt. Bitte den Menüpunkt noch einmal anklicken.",
}

export default {
  setup() {
    onMounted(async () => {
      if (route && route.name === "watchdog-page") {
        isAuthenticated.value = true
      } else {
        await checkAuth()

        // An expired or already redeemed token keeps the old behaviour: back to
        // the Concerton start page, which is the calendar dashboard and works -
        // the user simply clicks the menu entry again. A transport problem is
        // different: there we stay put and say so, because a silent jump is
        // indistinguishable from "the menu entry does nothing".
        if (!isAuthenticated.value && handoffFailure.value === "") {
          redirectToLogin()
        }
      }
    })

    const route = useRoute()

    const redirectToLogin = () => {
      location.href = import.meta.env.VITE_CONCERTON_URL
    }

    const processingAuthentication = ref(false)
    const isAuthenticated = ref(false)
    const handoffFailure = ref("")

    const handoffMessage = computed(() => HANDOFF_MESSAGES[handoffFailure.value] || "")

    const checkAuth = async () => {
      processingAuthentication.value = true

      // try to get one-time-token from uri-params
      const search = window.location.search.substring(1)
      const params = new URLSearchParams(search)
      if (params && params.has("ott")) {
        const oneTimeToken = params.get("ott")
        if (oneTimeToken) {
          const authService = new AuthService()
          await authService.getUserToken(oneTimeToken).then(data => {
            if (data.success && data.token) {
              sessionStorage.setItem("token", data.token)
              isAuthenticated.value = true
            } else {
              sessionStorage.removeItem("token")
              isAuthenticated.value = false

              // Only a rejected token leads to the jump below; everything else
              // leaves a message on screen.
              if (data.reason && data.reason !== "invalid") {
                handoffFailure.value = data.reason
              }

              // The jump itself stays silent for the user - landing on the
              // dashboard and clicking again is less confusing than a notice.
              // It must not be silent for whoever debugs it, though: not being
              // able to tell a rejected token from a transport problem is what
              // hid the rate limit for months.
              console.warn("Module entry failed, reason:", data.reason)
            }
          })
        }

        // Drops the token from the visible URL. Done through the URL API rather
        // than by cutting the string: the previous replace relied on the token
        // being the last parameter and left a dangling "?" or "&" behind.
        const url = new URL(window.location.href)
        url.searchParams.delete("ott")
        window.history.replaceState({}, document.title, url.toString())
      } else if (sessionStorage.getItem("token")) {
        isAuthenticated.value = true
        processingAuthentication.value = false
      }
    }

    return { isAuthenticated, handoffMessage }
  },
}
</script>
